/**
 * Step 6: 검색 설정
 */

import { SearchConfig } from '../../../api';
import { Checkbox } from '../../ui';

interface SearchConfigStepProps {
  search: SearchConfig;
  onSearchChange: (cfg: SearchConfig) => void;
  hasDatabase?: boolean;
}

export function SearchConfigStep({ search, onSearchChange, hasDatabase }: SearchConfigStepProps) {
  const set = <K extends keyof SearchConfig>(key: K, value: SearchConfig[K]) =>
    onSearchChange({ ...search, [key]: value });

  return (
    <div className="pipe-section form-item--md">
      <div className="form-label">
        검색 설정
        <span className='optional'>
          벡터 검색 시 적용할 파라미터를 설정하세요. 모든 소스에 공통 적용됩니다.
        </span>
      </div>

      {/* 검색 유형 */}
      <div className="search-config-section">
        <div className="choice-cards">
          <button
            type="button"
            role="radio"
            className={`choice-card${(!search.searchType || search.searchType === 'semantic') ? ' active' : ''}`}
            onClick={() => set('searchType', 'semantic')}
            >
            <span className="choice-card-radio" />
            <span className='choice-card-body'>
              <span className="choice-card-title">시맨틱 검색</span>
              <span className="choice-card-desc">벡터 임베딩을 사용하여 의미 기반으로 검색합니다. 유사도 임계값(0~1)이 정확하게 동작합니다.</span>
            </span>
          </button>
          <button
            type="button"
            role="radio"
            className={`choice-card${search.searchType === 'hybrid' ? ' active' : ''}`}
            onClick={() => set('searchType', 'hybrid')}
          >
            <span className="choice-card-radio" />
            <span className='choice-card-body'>
              <div className="choice-card-title">하이브리드 검색</div>
              <div className="choice-card-desc">
                벡터 + 텍스트(BM25) 검색을 결합합니다. 코드·고유명사 등 정확한 키워드 검색에 강합니다.
              </div>
            </span>
          </button>
        </div>
      </div>

      {/* Top K · 유사도 임계값 - 지식베이스 관리 모달과 같은 슬라이더 (km-*) */}
      <div className="km-pipeline-row pipe-search-sliders">
        <div className="km-field">
          <label>Top K (검색 결과 수)</label>
          <div className="km-slider-row">
            <input
              type="range"
              min={1}
              max={50}
              value={search.topK}
              onChange={(e) => set('topK', parseInt(e.target.value))}
              style={{ '--fill': `${((search.topK - 1) / 49) * 100}%` } as React.CSSProperties}
            />
            <span className="km-slider-val">{search.topK}</span>
          </div>
        </div>
        <div className="km-field">
          <label>유사도 임계값</label>
          <div className="km-slider-row">
            <input
              type="range"
              min={0}
              max={100}
              value={search.similarityThreshold * 100}
              onChange={(e) => set('similarityThreshold', parseInt(e.target.value) / 100)}
              style={{ '--fill': `${search.similarityThreshold * 100}%` } as React.CSSProperties}
            />
            <span className="km-slider-val">{(search.similarityThreshold * 100).toFixed(0)}%</span>
          </div>
        </div>
      </div>

      {/* 리랭킹 (DB 소스가 있으면 숨김) */}
      {!hasDatabase && (
        <div className="km-checkbox-row">
          <Checkbox
            checked={search.enableReranking}
            onChange={(e) => set('enableReranking', e.target.checked)}
            label="리랭킹 활성화 — 벡터 검색 후 AI가 결과를 재정렬합니다"
          />
        </div>
      )}
    </div>
  );
}
